import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS } from '@neke/core';
import type { ApiError } from '../api/client';
import { api, type InviteHistoryItem } from '../api/client';
import { useSession } from '../store/session';
import { ReportFile } from '../components/ReportFile';
import { track } from '../analytics';

/** Цвет левой полосы строки — по тому, как далеко ушла ссылка. */
function tierOf(item: InviteHistoryItem): 'introduce' | 'possible' | 'neutral' {
  if (item.reportReady) return 'introduce';
  if (item.status === 'ACCEPTED') return 'possible';
  return 'neutral';
}

/**
 * Статус строки. Оплата важнее пути ссылки: когда отчёт уже собран, человека
 * интересует не «принято ли приглашение», а открыт ли разбор и кому.
 */
function statusKeyOf(item: InviteHistoryItem): string {
  if (item.status === 'EXPIRED') return 'invites.statusExpired';
  if (item.status === 'REVOKED') return 'invites.statusRevoked';
  if (item.status === 'PENDING') return 'invites.statusPending';
  if (item.hasFullAccess) return 'invites.statusOpen';
  if (item.reportReady) {
    return item.partnerPaid ? 'invites.statusPartnerPaid' : 'invites.statusUnpaid';
  }
  return item.partnerCompletedTest ? 'invites.statusCompleted' : 'invites.statusAccepted';
}

export function Invites() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, locale, ready } = useSession();

  const [invites, setInvites] = useState<InviteHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Id скопированной строки, а не флаг: иначе подтверждение загорится на всех сразу.
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate('/login?next=/invites');
      return;
    }
    void api
      .myInvites()
      .then((list) => {
        setInvites(list);
        void track(ANALYTICS_EVENTS.invitesViewed, { count: list.length });
      })
      .catch((e: ApiError) => setError(e.message));
  }, [ready, user, navigate]);

  const when = (iso: string) =>
    new Date(iso).toLocaleDateString(locale === 'kk' ? 'kk-KZ' : 'ru-RU', {
      dateStyle: 'short',
    });

  const copy = (id: string, url: string) => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      void track(ANALYTICS_EVENTS.inviteLinkCopied);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1800);
    });
  };

  if (error) return <div className="page"><div className="card"><p>{error}</p></div></div>;
  if (!invites) return <div className="page"><p className="muted">{t('common.loading')}</p></div>;

  return (
    <div className="page">
      <div className="stack">
        <h1>{t('invites.title')}</h1>
        <p className="muted">{t('invites.lead')}</p>
      </div>

      {invites.length === 0 ? (
        <p className="notice">{t('invites.empty')}</p>
      ) : (
        invites.map((item) => {
          const dead = item.status === 'EXPIRED' || item.status === 'REVOKED';
          const isOwner = item.role === 'owner';
          // Заголовок строки: у автора — кому он отдавал ссылку, у принявшего —
          // от кого она пришла. Пометку автора второй стороне не показываем.
          const title = isOwner
            ? (item.recipientName ?? item.partner?.name ?? t('invites.noName'))
            : (item.partner?.name ?? t('invites.noName'));

          return (
            <div className={`match ${tierOf(item)}`} key={item.id}>
              <div className="match-head">
                <strong>{title}</strong>
                <span className={`flag ${dead ? 'hard' : ''}`}>{t(statusKeyOf(item))}</span>
              </div>

              {/* Показываем принявшего, только если это не тот, кому давали ссылку:
                  ради этого история и заводилась. */}
              {isOwner && item.partner && item.partner.name !== item.recipientName && (
                <p className="fine">{t('invites.openedBy', { name: item.partner.name })}</p>
              )}

              <p className="fine">{when(item.createdAt)}</p>

              {/* Отчёт куплен второй стороной: доступ личный и не передаётся,
                  но готовый файл она может прислать — это дешевле второй оплаты. */}
              {item.reportReady && !item.hasFullAccess && item.partnerPaid && item.partner && (
                <p className="fine">{t('invites.askPartner', { name: item.partner.name })}</p>
              )}

              {item.reportReady && (
                <Link className="btn btn-gold" to={`/pair/id/${item.id}`}>
                  {item.hasFullAccess ? t('invites.openReport') : t('invites.openPaywall')}
                </Link>
              )}

              {item.hasFullAccess && <ReportFile pairId={item.id} compact />}

              {isOwner && !dead && item.url && (
                <button className="btn btn-ghost" onClick={() => copy(item.id, item.url!)}>
                  {copiedId === item.id ? t('invites.copied') : t('invites.copyLink')}
                </button>
              )}
            </div>
          );
        })
      )}

      <Link to="/result" className="btn btn-ghost">
        {t('nav.profile')}
      </Link>
    </div>
  );
}
